import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardList } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { Modal } from '@/components/common/Modal';
import { PageHeader } from '@/components/layout/PageHeader';
import { TextField } from '@/components/forms/Field';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { leaveTypesApi } from '@/services/api/endpoints/hr';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { LeaveType, LeaveTypeInput } from '@/types/hr';
import { Badge, Days, SalaryStatusBadge } from './shared';

const EMPTY: LeaveTypeInput = {
  code: '',
  name: '',
  description: '',
  defaultDaysPerYear: null,
  paid: true,
  carryForward: false,
  maxCarryForwardDays: null,
  allowNegativeBalance: false,
  requiresDocument: false,
  minNoticeDays: 0,
  maxConsecutiveDays: null,
  countsNonWorkingDays: false,
  requiresHrApproval: true,
  displayOrder: 0,
};

/**
 * The kinds of leave a school grants, and the rules attached to each.
 *
 * <p>Types are retired, never deleted: balances and requests going back years
 * name their type, and removing one would leave that history pointing at
 * nothing. The list shows how many balances hold each type so the consequence of
 * retiring one is visible before it is done.
 */
export function LeaveTypesPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const manages = can(PERMISSIONS.LEAVE_TYPE_MANAGE);

  const [editing, setEditing] = useState<LeaveType | null>(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState<LeaveTypeInput>(EMPTY);

  const types = useQuery({
    queryKey: queryKeys.leaveTypes.list(false),
    queryFn: () => leaveTypesApi.list(false),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.leaveTypes.all });
  };

  const save = useMutation({
    mutationFn: () =>
      editing === null ? leaveTypesApi.create(form) : leaveTypesApi.update(editing.id, form),
    onSuccess: (type) => {
      toast.success(`${type.name} saved`);
      close();
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: 'ACTIVE' | 'INACTIVE' }) =>
      leaveTypesApi.setStatus(id, status),
    onSuccess: (type) => {
      toast.success(`${type.name} is now ${type.status.toLowerCase()}`);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  const open = (type: LeaveType | null) => {
    setEditing(type);
    setCreating(type === null);
    setForm(
      type === null
        ? EMPTY
        : {
            code: type.code,
            name: type.name,
            description: type.description ?? '',
            defaultDaysPerYear: type.defaultDaysPerYear ?? null,
            paid: type.paid,
            carryForward: type.carryForward,
            maxCarryForwardDays: type.maxCarryForwardDays ?? null,
            allowNegativeBalance: type.allowNegativeBalance,
            requiresDocument: type.requiresDocument,
            minNoticeDays: type.minNoticeDays,
            maxConsecutiveDays: type.maxConsecutiveDays ?? null,
            countsNonWorkingDays: type.countsNonWorkingDays,
            requiresHrApproval: type.requiresHrApproval,
            displayOrder: type.displayOrder,
          },
    );
  };

  const close = () => {
    setEditing(null);
    setCreating(false);
    setForm(EMPTY);
  };

  const columns: Column<LeaveType>[] = [
    {
      key: 'name',
      header: 'Type',
      render: (row) => (
        <span>
          <span className="block text-sm font-medium text-ink">{row.name}</span>
          <span className="block font-mono text-[11px] text-subtle">{row.code}</span>
        </span>
      ),
    },
    {
      key: 'entitlement',
      header: 'Days a year',
      className: 'text-right',
      render: (row) =>
        row.defaultDaysPerYear === undefined || row.defaultDaysPerYear === null ? (
          <span className="text-[13px] text-muted">Uncapped</span>
        ) : (
          <Days value={row.defaultDaysPerYear} />
        ),
    },
    {
      key: 'rules',
      header: 'Rules',
      render: (row) => (
        <span className="flex flex-wrap gap-1">
          <Badge tone={row.paid ? 'success' : 'warning'}>{row.paid ? 'Paid' : 'Unpaid'}</Badge>
          {row.requiresHrApproval && <Badge tone="info">HR approval</Badge>}
          {row.requiresDocument && <Badge tone="neutral">Document</Badge>}
          {row.carryForward && <Badge tone="neutral">Carries forward</Badge>}
          {row.minNoticeDays > 0 && <Badge tone="neutral">{row.minNoticeDays}d notice</Badge>}
          {row.countsNonWorkingDays && <Badge tone="neutral">Counts weekends</Badge>}
        </span>
      ),
    },
    {
      key: 'balances',
      header: 'In use',
      className: 'text-right',
      render: (row) => (
        <span className="text-[13px] text-muted">
          {row.balanceCount} balance{row.balanceCount === 1 ? '' : 's'}
        </span>
      ),
    },
    { key: 'status', header: 'Status', render: (row) => <SalaryStatusBadge status={row.status} /> },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) =>
        manages ? (
          <span className="flex justify-end gap-1.5">
            <Button size="sm" variant="secondary" onClick={() => open(row)}>
              Edit
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                setStatus.mutate({
                  id: row.id,
                  status: row.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE',
                })
              }
            >
              {row.status === 'ACTIVE' ? 'Retire' : 'Restore'}
            </Button>
          </span>
        ) : null,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Leave Types"
        description="What a school grants, and the rules that go with each kind."
        icon={<ClipboardList className="h-5 w-5" aria-hidden="true" />}
        action={manages ? <Button onClick={() => open(null)}>Add a type</Button> : undefined}
      />

      <Card className="overflow-hidden">
        <DataTable
          columns={columns}
          rows={types.data}
          rowKey={(row) => row.id}
          loading={types.isPending}
          error={types.error}
          onRetry={() => void types.refetch()}
          emptyTitle="No leave types"
          emptyDescription="A workable set is created the first time this page is opened."
        />
      </Card>

      {(editing !== null || creating) && (
        <Modal
          open
          onClose={close}
          title={editing === null ? 'Add a leave type' : `Edit ${editing.name}`}
        >
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              save.mutate();
            }}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Code"
                required
                // The code is what an import file or a payroll rule names, so it
                // is fixed once the type exists.
                disabled={editing !== null}
                value={form.code}
                onChange={(event) => setForm({ ...form, code: event.target.value })}
                placeholder="ANNUAL"
              />
              <TextField
                label="Name"
                required
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                placeholder="Annual Leave"
              />
            </div>

            <TextField
              label="Description"
              value={form.description ?? ''}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
            />

            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Days a year"
                type="number"
                step="0.5"
                min="0"
                value={form.defaultDaysPerYear ?? ''}
                onChange={(event) =>
                  setForm({
                    ...form,
                    defaultDaysPerYear:
                      event.target.value === '' ? null : Number(event.target.value),
                  })
                }
                hint="Leave blank for uncapped, as unpaid leave usually is"
              />
              <TextField
                label="Notice required (days)"
                type="number"
                min="0"
                max="365"
                value={form.minNoticeDays ?? 0}
                onChange={(event) => setForm({ ...form, minNoticeDays: Number(event.target.value) })}
                hint="Applies to self-service only; HR can always back-date"
              />
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label="Longest single absence (days)"
                type="number"
                min="1"
                value={form.maxConsecutiveDays ?? ''}
                onChange={(event) =>
                  setForm({
                    ...form,
                    maxConsecutiveDays:
                      event.target.value === '' ? null : Number(event.target.value),
                  })
                }
                hint="Blank for no limit"
              />
              <TextField
                label="Display order"
                type="number"
                min="0"
                value={form.displayOrder ?? 0}
                onChange={(event) => setForm({ ...form, displayOrder: Number(event.target.value) })}
              />
            </div>

            <fieldset className="space-y-2 border border-line p-3">
              <legend className="px-1 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">
                Rules
              </legend>
              <Toggle
                label="Paid"
                hint="Unpaid days become a payroll deduction"
                checked={form.paid ?? true}
                onChange={(paid) => setForm({ ...form, paid })}
              />
              <Toggle
                label="Needs HR approval after the manager"
                checked={form.requiresHrApproval ?? true}
                onChange={(requiresHrApproval) => setForm({ ...form, requiresHrApproval })}
              />
              <Toggle
                label="Needs a supporting document"
                checked={form.requiresDocument ?? false}
                onChange={(requiresDocument) => setForm({ ...form, requiresDocument })}
              />
              <Toggle
                label="Weekends and holidays inside the range are charged"
                hint="Off for annual leave; on for unpaid, where every day usually counts"
                checked={form.countsNonWorkingDays ?? false}
                onChange={(countsNonWorkingDays) => setForm({ ...form, countsNonWorkingDays })}
              />
              <Toggle
                label="Unused days carry forward"
                checked={form.carryForward ?? false}
                onChange={(carryForward) =>
                  setForm({
                    ...form,
                    carryForward,
                    // A carry-forward cap on a type that does not carry forward
                    // is refused by the server; clear it rather than let the
                    // user submit into a refusal.
                    maxCarryForwardDays: carryForward ? form.maxCarryForwardDays : null,
                  })
                }
              />
              {form.carryForward === true && (
                <TextField
                  label="At most (days)"
                  type="number"
                  step="0.5"
                  min="0"
                  value={form.maxCarryForwardDays ?? ''}
                  onChange={(event) =>
                    setForm({
                      ...form,
                      maxCarryForwardDays:
                        event.target.value === '' ? null : Number(event.target.value),
                    })
                  }
                />
              )}
              <Toggle
                label="Balance may go below zero"
                hint="Lets a new starter borrow against the year"
                checked={form.allowNegativeBalance ?? false}
                onChange={(allowNegativeBalance) => setForm({ ...form, allowNegativeBalance })}
              />
            </fieldset>

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="ghost" onClick={close}>
                Cancel
              </Button>
              <Button type="submit" disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-2 text-sm text-ink">
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 border-line"
      />
      <span>
        {label}
        {hint !== undefined && <span className="block text-[12px] text-muted">{hint}</span>}
      </span>
    </label>
  );
}
